import { usePermissions } from '@klareospace/pharos-lib-front-utils';
import { ORDER_UPDATE_FIELD_CATEGORY } from '@klareospace/pharos-lib-types';
import type { Order, OrderUpdateFieldCategory } from '@klareospace/pharos-lib-types';

import { resolveStatusBucket } from '../utils/status-bucket';
import type { StatusBucketKey } from '../types/order.types';

// ─── Constants ────────────────────────────────────────────────────────────────

export const ORDER_FIELD_EDIT_STATE = {
  EDITABLE: 'editable',
  HIDDEN: 'hidden',
  LOCKED: 'locked',
} as const;
export type OrderFieldEditState = (typeof ORDER_FIELD_EDIT_STATE)[keyof typeof ORDER_FIELD_EDIT_STATE];

export const ORDER_FIELD_LOCK_REASON = {
  IN_PROGRESS: 'in_progress',
  TERMINAL: 'terminal',
} as const;
export type OrderFieldLockReason = (typeof ORDER_FIELD_LOCK_REASON)[keyof typeof ORDER_FIELD_LOCK_REASON];

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseOrderEditabilityReturn {
  /** Whether the user holds orders:manage at all — gates the pencil affordance itself. */
  canManage: boolean;
  /** Resolves a field's edit state from its ORDER_UPDATE_FIELD_CATEGORY. */
  getFieldState: (category: OrderUpdateFieldCategory) => OrderFieldEditState;
  /** Why fields are locked, for tooltip selection — null when nothing is locked. */
  lockReason: OrderFieldLockReason | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveLockReason(bucket: StatusBucketKey): OrderFieldLockReason | null {
  if (bucket === 'DONE') return ORDER_FIELD_LOCK_REASON.TERMINAL;
  if (bucket === 'IN_PROGRESS') return ORDER_FIELD_LOCK_REASON.IN_PROGRESS;
  return null;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Combines the orders:manage permission with the order's status bucket to derive, per
 * field-update category, whether an editing affordance should render as editable, locked
 * (disabled + tooltip — in_progress lock or a never-editable category), or hidden (no
 * permission, or a terminal order — render exactly as read-only, no pencil at all).
 */
export function useOrderEditability(order: Order): UseOrderEditabilityReturn {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('orders:manage');
  const bucket = resolveStatusBucket(order.status);
  const lockReason = resolveLockReason(bucket);

  function getFieldState(category: OrderUpdateFieldCategory): OrderFieldEditState {
    if (!canManage) return ORDER_FIELD_EDIT_STATE.HIDDEN;
    // A terminal order has no editing affordance at all — render plain read-only, not a disabled pencil.
    if (lockReason === ORDER_FIELD_LOCK_REASON.TERMINAL) return ORDER_FIELD_EDIT_STATE.HIDDEN;
    if (category === ORDER_UPDATE_FIELD_CATEGORY.NON_EDITABLE) return ORDER_FIELD_EDIT_STATE.LOCKED;
    if (
      lockReason === ORDER_FIELD_LOCK_REASON.IN_PROGRESS &&
      category === ORDER_UPDATE_FIELD_CATEGORY.PLANNING_IMPACTING
    ) {
      return ORDER_FIELD_EDIT_STATE.LOCKED;
    }
    return ORDER_FIELD_EDIT_STATE.EDITABLE;
  }

  return { canManage, getFieldState, lockReason };
}
